/**
 * Wallpaper-group definitions.
 *
 * Each group is described by its lattice, rotation order, and tier. The actual
 * isometries that assemble the tiling from the fundamental domain live in
 * fundamental.ts — a group's geometry is fully determined by its id.
 */
import type { LatticeType } from './engine';

export type WallpaperGroupId =
  | 'p1'
  | 'p2'
  | 'pm'
  | 'pg'
  | 'pmm'
  | 'pmg'
  | 'pgg'
  | 'cm'
  | 'cmm'
  | 'p4'
  | 'p4m'
  | 'p4g'
  | 'p3'
  | 'p3m1'
  | 'p31m'
  | 'p6'
  | 'p6m';

export type RotationOrder = 1 | 2 | 3 | 4 | 6;

export interface WallpaperGroup {
  id: WallpaperGroupId;
  /** plain-English name shown in the UI */
  label: string;
  /** terse description for tooltips / param summary */
  blurb: string;
  lattice: LatticeType;
  rotationOrder: RotationOrder;
  free: boolean;
}

export const WALLPAPER_GROUPS: Record<WallpaperGroupId, WallpaperGroup> = {
  // ── Oblique ──
  p1: { id: 'p1', label: 'Sliding', blurb: 'Pure translation — every copy slides, no turns or flips.', lattice: 'oblique', rotationOrder: 1, free: true },
  p2: { id: 'p2', label: 'Half turn', blurb: 'Alternate copies are turned 180° — neighbours lock head-to-toe.', lattice: 'oblique', rotationOrder: 2, free: false },

  // ── Rectangular ──
  pm: { id: 'pm', label: 'Mirror rows', blurb: 'Alternate columns are mirrored across a vertical line.', lattice: 'rectangular', rotationOrder: 1, free: false },
  pg: { id: 'pg', label: 'Glide', blurb: 'Alternate columns are flipped — a glide reflection along the row.', lattice: 'rectangular', rotationOrder: 1, free: false },
  pmm: { id: 'pmm', label: 'Mirror grid', blurb: 'Mirror lines run both ways — a kaleidoscope of rectangles.', lattice: 'rectangular', rotationOrder: 2, free: true },
  pmg: { id: 'pmg', label: 'Mirror & turn', blurb: 'Mirror columns interleaved with half-turns.', lattice: 'rectangular', rotationOrder: 2, free: false },
  pgg: { id: 'pgg', label: 'Double glide', blurb: 'Two glide directions woven with half-turns.', lattice: 'rectangular', rotationOrder: 2, free: false },

  // ── Centred rectangular (rhombus) ──
  cm: { id: 'cm', label: 'Diamond mirror', blurb: 'Alternate diamonds mirror across an axis.', lattice: 'centred-rect', rotationOrder: 1, free: true },
  cmm: { id: 'cmm', label: 'Diamond kaleidoscope', blurb: 'Mirror lines on both axes plus half-turns.', lattice: 'centred-rect', rotationOrder: 2, free: false },

  // ── Square ──
  p4: { id: 'p4', label: 'Quarter turn', blurb: 'Four copies spin 90° about a shared corner.', lattice: 'square', rotationOrder: 4, free: true },
  p4m: { id: 'p4m', label: 'Quarter kaleidoscope', blurb: 'Four-way turns with mirror lines — full square symmetry.', lattice: 'square', rotationOrder: 4, free: false },
  p4g: { id: 'p4g', label: 'Pinwheel mirror', blurb: 'Four-way turns with diagonal mirrors — a pinwheel of reflections.', lattice: 'square', rotationOrder: 4, free: false },

  // ── Hexagonal ──
  p3: { id: 'p3', label: 'Three-way turn', blurb: 'Neighbours turn 120° around every corner.', lattice: 'hexagonal', rotationOrder: 3, free: true },
  p3m1: { id: 'p3m1', label: 'Three-way snowflake', blurb: 'Three-way turns with mirror lines through the corners.', lattice: 'hexagonal', rotationOrder: 3, free: false },
  p31m: { id: 'p31m', label: 'Three-way star', blurb: 'Three-way turns with mirror lines through the edges.', lattice: 'hexagonal', rotationOrder: 3, free: false },
  p6: { id: 'p6', label: 'Six-way turn', blurb: 'Neighbours turn 60° around every corner — a snowflake spin.', lattice: 'hexagonal', rotationOrder: 6, free: false },
  p6m: { id: 'p6m', label: 'Snowflake', blurb: 'Six-way turns with mirror lines — full snowflake symmetry.', lattice: 'hexagonal', rotationOrder: 6, free: false },
};

export function getWallpaperGroup(id: WallpaperGroupId): WallpaperGroup {
  const g = WALLPAPER_GROUPS[id];
  if (!g) throw new Error(`Unknown wallpaper group: ${id}`);
  return g;
}
